import {debounce, difference, findKey, throttle, uniq} from 'lodash';
import memoizeOne from 'memoize-one';
import {AuthInfo, AuthorizerGenerator} from 'pusher-js';
import React, {Component} from 'react';
import {connect} from 'react-redux';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {PusherSocket} from '../../../srcCommon/services/push/pusher';
import {PushSocket} from '../../../srcCommon/services/push/pushSocket';
import {inAnimationFrameOrHiddenCallbackAsync} from '../../helpers/async/inAnimationFrame';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapStateToProps, wrapInShallowSelector} from '../../helpers/reselectHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {PusherConfigModel} from '../../models/api/pushConfigModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {info} from '../../services/debug';
import {postAsync} from '../../services/http/http';
import {
  createChatUserOnlineStatusAction,
  deleteChatUserOnlineStatusAction,
} from '../../store/data/chat/chatUserOnlineActions';
import {deleteChatPrecog, updateChatPrecog} from '../../store/data/chatPrecogs/chatPrecogsActions';
import {getCompany} from '../../store/data/company/companySelectors';
import {getChatPushConfig} from '../../store/data/dataSelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {getHasNewPusherOnReconnect} from '../../store/featureFlags/featureFlagsSelectors';
import {getViewerPanelsChatConversationIds} from '../../store/panels/viewer/viewerPanelSelectors';
import {ChatPushContext} from './chatPushContext';

/*
 * Props.
 */

interface ChatPushStateProps {
  chatConfig?: PusherConfigModel;
  company: CompanyModel;
  user: TeammateModel;
  viewerPanelsConversationIds: ReadonlyArray<number>;
  hasNewPusherOnReconnect: boolean;
}

interface ChatPushProps extends ChatPushStateProps, ResolveDispatchProps<typeof dispatchProps> {}

/*
 * Component.
 */

type Unsubscribe = () => void;

// Max time to wait before disconnecting from the Pusher chat instance when no chat conversation is open.
// We do this so as to avoid unnecessary connections when it's not used.
const inactivityGracePeriod = 5 * 60 * 1000;

// Max time after which a precog is deleted if no continue message is received.
const chatPrecogTimeout = 5000;

class ChatPush extends Component<ChatPushProps> {
  constructor(props: ChatPushProps) {
    super(props);

    const {chatConfig} = this.props;

    if (!chatConfig) {
      return;
    }

    this.chatSocket = this.makeChatPushSocket(chatConfig);
  }

  // We don't want to keep a Pusher chat connection open for nothing if the user is not actively interacting with Front chat conversations.
  private disconnectDelayed = throttle(
    () => {
      if (this.isConnected) {
        this.chatSocket!.disconnect();
        this.isConnected = false;
      }

      this.isActive = false;
    },
    inactivityGracePeriod,
    {leading: false, trailing: true},
  );

  private isActive = false;
  private isConnected = false;

  private readonly chatSocket: PushSocket | undefined;

  private conversationChatMap: {[conversationId: number]: {chatId: string; roomName: string}} = {};
  private authenticationTokens: {[roomName: string]: AuthInfo} = {};

  // Map containing delayed functions that delete expired precogs.
  private deleteChatPrecogDebouncedMap: {[conversationId: number]: () => void} = {};

  deleteChatPrecogDebounced(conversationId: number) {
    // Create a debounced precog delete function for this conversationId if it doesn't exist.
    if (!(conversationId in this.deleteChatPrecogDebouncedMap)) {
      this.deleteChatPrecogDebouncedMap[conversationId] = debounce(
        () =>
          inAnimationFrameOrHiddenCallbackAsync(
            () => {
              this.props.deleteChatPrecog(conversationId);
              // This throttled chat precog deletion has triggered, we don't need it anymore now.
              delete this.deleteChatPrecogDebouncedMap[conversationId];
            },
            {source: TaskSourcesEnum.PUSHER},
          ),
        chatPrecogTimeout,
        {leading: false, trailing: true},
      );
    }

    // Schedule/bump the delayed execution.
    this.deleteChatPrecogDebouncedMap[conversationId]();
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    if (!this.chatSocket) {
      return;
    }

    globalWindow.addEventListener('offline', () => {
      if (this.isConnected) {
        this.chatSocket!.disconnect();
        this.isConnected = false;
      }
    });
    globalWindow.addEventListener('online', () => {
      this.updateConnectionStatus();
    });

    // Register lifecycle events.
    this.chatSocket.on('connected', async () => {
      info('Chat push connected.');
      this.resubscribeToRooms();
    });

    this.chatSocket.on('createChatUserOnlineStatus', (conversationId: any) => {
      const frontConversationId = this.getFrontConversationId(conversationId);
      if (!frontConversationId) {
        return;
      }
      this.props.createChatUserOnlineStatusAction(frontConversationId);
    });

    this.chatSocket.on('deleteChatUserOnlineStatus', (conversationId: any) => {
      const frontConversationId = this.getFrontConversationId(conversationId);
      if (!frontConversationId) {
        return;
      }
      this.props.deleteChatUserOnlineStatusAction(frontConversationId);
    });

    this.chatSocket.on('disconnected', async () => {
      info('Chat push disconnected.');
      this.unsubscribeFromRooms();
    });

    this.isActive = this.props.viewerPanelsConversationIds.length > 0;

    // Maybe connect the socket.
    if (navigator.onLine && this.isActive) {
      this.connectSocket();
    }
  }

  componentDidUpdate() {
    if (!this.chatSocket) {
      return;
    }

    this.updateConnectionStatus();

    if (this.isActive && this.isConnected) {
      this.resubscribeToRooms();
    }
  }

  private getFrontConversationId(chatConversationId: string): number | undefined {
    const conversationId: string | undefined = findKey(this.conversationChatMap, [
      'chatId',
      chatConversationId,
    ]);

    if (conversationId) {
      return Number(conversationId);
    }
    return undefined;
  }

  private updateConnectionStatus() {
    const hasSubscriptions = this.props.viewerPanelsConversationIds.length > 0;
    if (hasSubscriptions) {
      this.isActive = true;
      this.disconnectDelayed.cancel();
      // Schedule a disconnect in the future
    } else if (this.isActive) {
      this.disconnectDelayed();
    }

    if (!this.isConnected && this.isActive && navigator.onLine) {
      this.connectSocket();
    }
  }

  componentWillUnmount() {
    if (!this.chatSocket) {
      return;
    }

    this.unsubscribeFromRooms();

    // Cleanup the socket.
    this.chatSocket.removeAllListeners();
    this.chatSocket.disconnect();
  }

  private makeChatPushSocket(config: PusherConfigModel): PushSocket {
    const chatAuthorizer: AuthorizerGenerator = (channel) => ({
      authorize: (socketId, callback) => {
        const roomName = channel.name.replace(/^presence-/, '');
        return callback(false, this.authenticationTokens[roomName]);
      },
    });

    // Front chat doesn't support Socket.IO
    return new PusherSocket(
      config.key,
      config.cluster,
      config.roomsPrefix,
      {
        shouldRegisterGlobalChannels: false,
        shouldUpdateGlobalSocketId: false,
      },
      chatAuthorizer,
      () => true,
    );
  }

  private connectSocket() {
    // Connect the driver.
    this.chatSocket!.connect(this.props.hasNewPusherOnReconnect);
    this.isConnected = true;

    this.chatSocket!.onDirectPushEvent('chat_precog_updated', (data) => {
      const chatConversationId = data.chat_conversation_id;
      const conversationId: string | undefined = findKey(this.conversationChatMap, [
        'chatId',
        chatConversationId,
      ]);
      if (!conversationId) {
        return;
      }

      inAnimationFrameOrHiddenCallbackAsync(
        () => {
          this.props.updateChatPrecog(Number(conversationId));
        },
        {source: TaskSourcesEnum.PUSHER},
      );

      this.deleteChatPrecogDebounced(Number(conversationId));
    });

    this.resubscribeToRooms();
  }

  /*
   * Subscriptions.
   */

  private async resubscribeToRooms() {
    const conversationIds = this.props.viewerPanelsConversationIds;
    return this.updateSubscriptions(conversationIds);
  }

  private unsubscribeFromRooms() {
    return this.updateSubscriptions([]);
  }

  private subscriptions: {[conversationId: number]: Unsubscribe} = {};

  private async updateSubscriptions(conversationIds: ReadonlyArray<number>) {
    // Get the previous and next set of conversationIds we want to be subscribed to.
    const previousConversations = Object.keys(this.subscriptions).map(Number);
    const nextConversations = uniq(conversationIds);

    // Compute the difference.
    const subscribeToConversations = difference(nextConversations, previousConversations);
    const unsubscribeFromConversations = difference(previousConversations, nextConversations);

    // Unsubscribe.
    unsubscribeFromConversations.forEach((conversationId) => {
      try {
        if (conversationId in this.conversationChatMap) {
          const {roomName} = this.conversationChatMap[conversationId];
          delete this.authenticationTokens[roomName];
          delete this.conversationChatMap[conversationId];
        }

        const unsubscribe = this.subscriptions[conversationId];
        if (!unsubscribe) {
          return;
        }

        // Try to unsubscribe.
        unsubscribe();
      } catch (error) {
        reportToBugsnag(new ExtendedError(`Failed to unsubscribe from chat room '${conversationId}'`, error));
      } finally {
        // Remove from the subscriptions no matter what.
        delete this.subscriptions[conversationId];
      }
    });

    // Bail if we're not connected.
    if (!this.chatSocket!.isConnected) {
      return;
    }

    // Subscribe.
    const promises = subscribeToConversations.map(async (conversationId) => {
      const result = await postAsync(`${this.props.company.url}/pusher/auth_chat?is_presence=true`, {
        socket_id: this.chatSocket!.id,
        conversation_id: conversationId,
      });

      const frontChatConversationId = result.front_chat_conversation_id;
      const roomName = result.room_name;
      const {authentication} = result;
      this.authenticationTokens[roomName] = authentication;
      this.conversationChatMap[conversationId] = {roomName, chatId: frontChatConversationId};

      const unsubscribe = this.chatSocket!.subscribe(roomName);
      this.subscriptions[conversationId] = unsubscribe;
    });

    await Promise.all(promises);
  }

  /*
   * Context.
   */

  private readonly buildContextValue = memoizeOne(() => ({
    emitChatPrecog: (conversationId: number, data: any) =>
      this.chatSocket &&
      this.chatSocket.emitDirectEvent(
        this.conversationChatMap[conversationId]!.roomName,
        'chat_reply_precog_updated',
        data,
      ),
  }));

  /*
   * Render.
   */

  render() {
    const {children} = this.props;
    const contextValue = this.buildContextValue();
    return <ChatPushContext.Provider value={contextValue}>{children}</ChatPushContext.Provider>;
  }
}

/*
 * Redux connection.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  chatConfig: getChatPushConfig,
  company: getCompany,
  user: getUser,
  viewerPanelsConversationIds: wrapInShallowSelector(getViewerPanelsChatConversationIds),
  hasNewPusherOnReconnect: getHasNewPusherOnReconnect,
}));

const dispatchProps = {
  updateChatPrecog,
  deleteChatPrecog,
  createChatUserOnlineStatusAction,
  deleteChatUserOnlineStatusAction,
};

export const ChatPushContainer = connect(mapStateToProps, dispatchProps)(ChatPush);
